import { useId, useState } from 'react';
import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { VisuallyHidden } from '@understory-io/leaf/visually-hidden';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { Link } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { Image } from '~/ui/image';
import { TextLink } from '~/ui/link';
import { FormattedLocation } from '~/ui/formatted';
import { EventTime, firstLetterCapStyle } from '~/ui/event-time';
import { StackedFlagAvatars } from '~/ui/event-card/stacked-avatars';
import {
  AvailabilityLabel,
  type SeatCounterVisibility,
} from '~/ui/availability-label/availability-label';
import type {
  UpcomingDateAggregateEvent,
  UpcomingDateEvent,
} from './types';

export type {
  UpcomingDateAggregateEvent,
  UpcomingDateEvent,
  UpcomingDateSession,
} from './types';

export type UpcomingDatesVariant = 'default' | 'expanded';

type CommonProps = {
  seeAllHref?: string;
  seatCounterVisibility?: SeatCounterVisibility;
};

type UpcomingDatesSectionProps = CommonProps &
  (
    | { variant?: 'default'; events: UpcomingDateEvent[] }
    | { variant: 'expanded'; events: UpcomingDateAggregateEvent[] }
  );

export function UpcomingDatesSection(props: UpcomingDatesSectionProps) {
  // Name the <section> via its heading so it's exposed as an ARIA `region`
  // with an accessible name (the heading text, "Upcoming dates"). `useId`
  // keeps the heading id unique when the page renders more than one instance.
  // Called before the early return so the hook order stays stable.
  const headingId = useId();

  if (props.events.length === 0) return null;

  const { seeAllHref, seatCounterVisibility } = props;
  const variant: UpcomingDatesVariant = props.variant ?? 'default';

  return (
    <Stack as="section" gap="xlarge" aria-labelledby={headingId}>
      <div className={headingRowStyles}>
        <Text as="h2" id={headingId} style="subheadline-large">
          <FormattedMessage
            defaultMessage="Upcoming dates"
            description="Heading of the upcoming dates section on the experience details page"
          />
        </Text>
        {variant === 'expanded' && seeAllHref && (
          // "See all dates" points to the full availability page. Whether that
          // is served by vnext or the legacy app is the route registry's call
          // (`external`), and `TextLink` already falls back to a plain anchor
          // for anything the registry doesn't own — so no `reloadDocument`
          // override is needed here.
          <TextLink to={seeAllHref} style="body-large-regular">
            <FormattedMessage
              defaultMessage="See all dates"
              description="Link in the upcoming dates section heading that opens the full availability list"
            />
          </TextLink>
        )}
      </div>

      <ol
        className={cx(
          listBaseStyles,
          variant === 'expanded' ? listExpandedStyles : listDefaultStyles
        )}
      >
        {props.variant === 'expanded'
          ? props.events.map((event) => (
              <li
                key={event.eventId}
                className={cx(listItemBaseStyles, listItemExpandedStyles)}
              >
                <UpcomingDateCard
                  variant="expanded"
                  event={event}
                  seatCounterVisibility={seatCounterVisibility}
                />
              </li>
            ))
          : props.events.map((event) => (
              <li
                key={event.eventId}
                className={cx(listItemBaseStyles, listItemDefaultStyles)}
              >
                <UpcomingDateCard
                  variant="default"
                  event={event}
                  seatCounterVisibility={seatCounterVisibility}
                />
              </li>
            ))}
      </ol>

      {variant === 'default' && seeAllHref && (
        <LinkButton
          href={seeAllHref}
          appearance="secondary"
          size="large"
          className={seeAllButtonStyles}
        >
          <FormattedMessage
            defaultMessage="See all dates"
            description="Button beneath the upcoming dates list that opens the full availability list"
          />
        </LinkButton>
      )}
    </Stack>
  );
}

type UpcomingDateCardProps = {
  seatCounterVisibility?: SeatCounterVisibility;
} & (
  | { variant: 'default'; event: UpcomingDateEvent }
  | { variant: 'expanded'; event: UpcomingDateAggregateEvent }
);

function UpcomingDateCard(props: UpcomingDateCardProps) {
  const { event, variant, seatCounterVisibility } = props;
  const isSoldOut = event.status === 'soldOut';
  // Loader-built deep-link. For shared experiences this points to the owner
  // storefront's checkout flow with the distributor + event pre-filled.
  const checkoutHref = event.checkoutHref;
  // Card link target differs by variant. `expanded` cards (home page, across
  // experiences) link to each experience's details page and stay clickable
  // even when sold out — the Book button below still drives checkout. `default`
  // cards (a single experience's own dates) link straight into checkout, so
  // they're suppressed when sold out.
  const cardLink =
    props.variant === 'expanded'
      ? { href: props.event.experienceHref, show: true }
      : { href: checkoutHref, show: !isSoldOut };

  return (
    <article
      className={cx(
        cardBaseStyles,
        variant === 'expanded' ? cardExpandedStyles : cardDefaultStyles
      )}
    >
      {cardLink.show && (
        <Link to={cardLink.href} className={stretchedLinkStyles}>
          <VisuallyHidden>
            {props.variant === 'expanded' ? (
              <FormattedMessage
                defaultMessage="View {experienceName}"
                description="Visually-hidden accessible label for the upcoming date card link that opens the experience details page"
                values={{ experienceName: props.event.experienceName }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Book this date"
                description="Visually-hidden accessible label for the upcoming date card link when no experience name is shown"
              />
            )}
          </VisuallyHidden>
        </Link>
      )}

      {props.variant === 'expanded' && (
        <ExpandedImage src={props.event.experienceImageUrl ?? null} />
      )}

      <Stack gap="xsmall" className={infoStackStyles}>
        {props.variant === 'expanded' && (
          <Stack direction="row" align="center" gap="small">
            <Text as="p" style="subheadline-small">
              {props.event.experienceName}
            </Text>
            {event.locales.length > 0 && (
              <StackedFlagAvatars locales={event.locales} />
            )}
          </Stack>
        )}

        {variant === 'default' ? (
          // `default` variant: stacked date (`subheadline-small`) above the
          // details line. Render-prop preserves the title's tighter heading
          // typography while letting `<EventTime>` choose whether the details
          // line is a time range, a date range, or "N sessions, Y weeks".
          <EventTime sessions={event.sessions}>
            {({ title, details }) => (
              <Stack gap="tiny" className={firstLetterCapStyle}>
                <Text
                  as="p"
                  style="subheadline-small"
                  color={isSoldOut ? 'tertiary' : undefined}
                  className={isSoldOut ? strikethroughStyles : undefined}
                  suppressHydrationWarning
                >
                  {title}
                </Text>
                {details && (
                  <Text
                    as="p"
                    style="body-large-regular"
                    color={isSoldOut ? 'tertiary' : 'secondary'}
                    suppressHydrationWarning
                  >
                    {details}
                  </Text>
                )}
              </Stack>
            )}
          </EventTime>
        ) : (
          // `expanded` variant: title + details on one row on tablet+, stacked
          // on mobile. Same render-prop strategy so the title/details split
          // mirrors the design's existing rhythm.
          <EventTime sessions={event.sessions}>
            {({ title, details }) => (
              <div className={cx(dateTimeRowStyles, firstLetterCapStyle)}>
                <Text
                  as="p"
                  style="body-large-regular"
                  suppressHydrationWarning
                >
                  {title}
                </Text>
                {details && (
                  <Text
                    as="p"
                    style="body-large-regular"
                    suppressHydrationWarning
                  >
                    {details}
                  </Text>
                )}
              </div>
            )}
          </EventTime>
        )}

        {variant === 'default' ? (
          // Location and the sold-out label share one row: location on the
          // left, "Sold out" pushed to the right (`margin-left: auto`) so it
          // still aligns right when there's no location.
          (event.location || isSoldOut) && (
            <div className={defaultLocationRowStyles}>
              {event.location && (
                <Text
                  as="p"
                  style="body-large-regular"
                  color={isSoldOut ? 'tertiary' : 'secondary'}
                  className={defaultLocationTextStyles}
                >
                  <FormattedLocation location={event.location} />
                </Text>
              )}
              {isSoldOut && (
                <Text
                  as="p"
                  style="body-large-regular"
                  color="tertiary"
                  className={soldOutLabelStyles}
                >
                  <FormattedMessage
                    defaultMessage="Sold out"
                    description="Label on a default-variant upcoming date card when the event is sold out"
                  />
                </Text>
              )}
            </div>
          )
        ) : (
          event.location && (
            <Text
              as="p"
              style="body-large-regular"
              color="secondary"
              className={css({
                overflow: 'hidden',
              })}
            >
              <FormattedLocation location={event.location} />
            </Text>
          )
        )}

        {variant === 'default' && event.description && (
          <Text
            as="p"
            style="body-large-regular"
            color={isSoldOut ? 'tertiary' : 'secondary'}
            className={descriptionStyles}
          >
            {event.description}
          </Text>
        )}
      </Stack>

      {variant === 'expanded' && (
        <div className={availabilityWrapStyles}>
          <AvailabilityLabel
            status={event.status}
            availableSeats={event.availableSeats}
            seatCounterVisibility={seatCounterVisibility}
            hideSeatCount={event.hideSeatCount}
            bookingMaxBelowSeatCount={event.bookingMaxBelowSeatCount}
            textStyle="body-large-regular"
          />
        </div>
      )}

      {variant === 'expanded' && (
        <div className={bookButtonWrapStyles}>
          <LinkButton
            href={checkoutHref}
            appearance={'primary'}
            size="large"
            disabled={isSoldOut}
          >
            <FormattedMessage
              defaultMessage="Book"
              description="Button on an upcoming date card that starts the checkout flow"
            />
          </LinkButton>
        </div>
      )}
    </article>
  );
}

/**
 * Expanded-card thumbnail. Always renders the wrapper so cards line up even
 * when an experience has no image — visitors see an empty placeholder square
 * instead of the date/info collapsing into where the image used to sit. Falls
 * back to the same placeholder if the URL is present but the image fails to
 * load (broken upload, CDN hiccup).
 */
function ExpandedImage({ src }: { src: string | null }) {
  const [errored, setErrored] = useState(false);
  const showImage = src !== null && !errored;
  return (
    <div className={imageWrapStyles}>
      {showImage && (
        <Image
          src={src}
          alt=""
          width={113}
          height={113}
          className={imageStyles}
          onError={() => setErrored(true)}
        />
      )}
    </div>
  );
}

const headingRowStyles = css({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 'medium',
  width: 'full',
});

// On mobile both variants use a horizontal scroll-snap row. `overflow-x: auto`
// forces `overflow-y: auto` too, so we pad the scroll area for the cards'
// shadow.large (extends ~11px above, ~19px below, ~15px sideways) and counter
// it with negative margin so the section's outer layout stays flush.
const listBaseStyles = css({
  listStyle: 'none',
  margin: 'none',
  padding: 'none',
  display: 'flex',
  gap: 'medium',
  width: 'full',
  minWidth: '[0]',
  overflowX: { base: 'auto', tablet: 'visible' },
  scrollSnapType: { base: '[x mandatory]', tablet: '[none]' },
  scrollPaddingInline: { base: 'small', tablet: 'none' },
  paddingTop: { base: 'small', tablet: 'none' },
  paddingBottom: { base: 'large', tablet: 'none' },
  marginTop: { base: '-small', tablet: 'none' },
  marginBottom: { base: '-large', tablet: 'none' },
});

const listDefaultStyles = css({
  flexDirection: 'row',
});

const listExpandedStyles = css({
  flexDirection: { base: 'row', tablet: 'column' },
});

const listItemBaseStyles = css({
  flexShrink: 0,
  scrollSnapAlign: { base: 'start', tablet: 'none' },
});

const listItemDefaultStyles = css({
  width: { base: '[min(85%, 320px)]', tablet: '[auto]' },
  flexBasis: { base: '[auto]', tablet: '[0]' },
  flexGrow: { base: 0, tablet: 1 },
  minWidth: { tablet: '[0]' },
});

const listItemExpandedStyles = css({
  width: { base: '[min(85%, 320px)]', tablet: 'full' },
  flexShrink: { base: 0, tablet: 1 },
  minWidth: { tablet: '[0]' },
});

const cardBaseStyles = css({
  position: 'relative',
  // The list's default `align-items: stretch` makes every <li> match the
  // tallest sibling, so cards in a row line up — but the <article> inside
  // would otherwise hug its content. `height: full` makes it fill the <li>.
  height: 'full',
  minWidth: '[0]',
  background: 'surface.raised',
  padding: 'large',
  borderRadius: 'large',
  shadow: 'large',
  overflow: 'hidden',
  display: 'flex',
});

const cardDefaultStyles = css({
  flexDirection: 'column',
  // Default variant is rendered in a horizontally-scrollable row (single
  // experience's upcoming dates on the experience details page). Cap the
  // card width so visitors see multiple cards at once on wide viewports
  // instead of one stretched card.
  maxW: '[570px]',
});

const cardExpandedStyles = css({
  flexDirection: { base: 'column', tablet: 'row' },
  alignItems: { tablet: 'center' },
  gap: { base: 'xsmall', tablet: 'medium' },
});

const stretchedLinkStyles = css({
  position: 'absolute',
  inset: '[0]',
  zIndex: '[1]',
  borderRadius: 'large',
  _focusVisible: {
    outline: '[2px solid]',
    outlineColor: 'border.focus',
    outlineOffset: '[-2px]',
  },
});

const imageWrapStyles = css({
  display: { base: 'none', tablet: 'block' },
  position: 'relative',
  zIndex: '[2]',
  flexShrink: 0,
  width: '[113px]',
  aspectRatio: '[1]',
  borderRadius: 'small',
  overflow: 'hidden',
  // Acts as the placeholder when no image is provided or the image fails to
  // load — visitors see a neutral square instead of the date/info shifting
  // into the missing slot.
  background: 'surface.background',
});

const imageStyles = css({
  width: 'full',
  height: 'full',
  objectFit: 'cover',
});

const infoStackStyles = css({
  position: 'relative',
  zIndex: '[2]',
  flex: '1',
  minWidth: '[0]',
  pointerEvents: 'none',
});

const dateTimeRowStyles = css({
  display: 'flex',
  flexDirection: { base: 'column', tablet: 'row' },
  gap: { base: 'none', tablet: '[12px]' },
});

const strikethroughStyles = css({
  textDecoration: 'line-through',
});

// Default-variant location row: location on the left, "Sold out" label on the
// right. Mirrors the location + AvailabilityLabel row in `EventCard`.
const defaultLocationRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'start',
  gap: 'small',
});

const defaultLocationTextStyles = css({
  minWidth: '[0]',
  overflow: 'hidden',
});

// Two lines keep a long host text from stretching one card far past its
// neighbours; the full text is on the checkout date step the card links to.
const descriptionStyles = css({
  lineClamp: '2',
});

// `margin-left: auto` keeps the label right-aligned even when there's no
// location text on the row.
const soldOutLabelStyles = css({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  marginLeft: 'auto',
});

const availabilityWrapStyles = css({
  display: { base: 'none', tablet: 'flex' },
  position: 'relative',
  zIndex: '[2]',
  flexShrink: 0,
  pointerEvents: 'none',
});

const bookButtonWrapStyles = css({
  display: { base: 'none', tablet: 'flex' },
  position: 'relative',
  zIndex: '[2]',
  flexShrink: 0,
});

const seeAllButtonStyles = css({
  alignSelf: { base: 'stretch', tablet: 'flex-start' },
});
